export interface Property {
  id: string;
  name: string;
  address: string;
  city: string;
  state: string;
  zipCode: string;
  type: 'apartment' | 'house' | 'condo' | 'townhouse' | 'multi-family';
  description?: string;
  image?: string;
  purchaseDate?: string;
  purchasePrice?: number;
  /** Non-depreciable land portion of the cost basis. Only the building
   * depreciates, so this is subtracted from purchasePrice for depreciation. */
  landValue?: number;
  driveFolderId?: string;
}

export type UtilityType = 'water' | 'gas' | 'electric';

export type ApplianceType =
  | 'refrigerator' | 'stove_oven' | 'dishwasher' | 'washer' | 'dryer'
  | 'hvac' | 'water_heater' | 'microwave' | 'garbage_disposal' | 'furnace'
  | 'air_conditioner' | 'range_hood' | 'freezer' | 'other';

export type ApplianceCondition = 'excellent' | 'good' | 'fair' | 'replace_soon';

export interface Appliance {
  id: string;
  propertyId: string;
  unitId?: string;
  type: ApplianceType;
  brand?: string;
  modelNumber?: string;
  serialNumber?: string;
  purchaseDate?: string;
  warrantyExpiration?: string;
  condition: ApplianceCondition;
  notes?: string;
}

/** A utility account the landlord pays on a property (reference details so the
 * monthly bill is quick to encode as a Utilities expense). */
export interface UtilityAccount {
  id: string;
  propertyId: string;
  /** Optional: set when the meter serves one specific unit (separately metered). */
  unitId?: string;
  type: UtilityType;
  provider?: string;
  accountNumber?: string;
  loginUrl?: string;
  notes?: string;
}

/** Possible occupancy states for a unit.
 *  - `occupied` / `vacant`: derived from lease presence, never set by hand.
 *  - `maintenance`: unit temporarily offline for routine work (counts as vacancy loss).
 *  - `renovation`: major rehab; excluded from vacancy loss by default.
 *  - `owner_hold`: owner using or holding the unit; excluded from vacancy loss.
 *  - `unrentable`: unit cannot be rented (legal, structural); excluded from vacancy loss.
 */
export type UnitStatus =
  | 'occupied'
  | 'vacant'
  | 'maintenance'
  | 'renovation'
  | 'owner_hold'
  | 'unrentable';

/** Statuses that exclude a unit from vacancy loss calculations. The unit is
 *  intentionally unavailable, so the empty period is NOT market vacancy. */
export const VACANCY_EXCLUDED_STATUSES: ReadonlySet<UnitStatus> = new Set([
  'renovation', 'owner_hold', 'unrentable',
]);

export interface Unit {
  id: string;
  propertyId: string;
  unitNumber: string;
  bedrooms: number;
  bathrooms: number;
  squareFeet: number;
  monthlyRent: number;
  status: UnitStatus;
  description?: string;
}

export type LeaseStatus = 'active' | 'paused' | 'ended';
export type LeaseType = 'fixed' | 'month_to_month';

export interface Lease {
  id: string;
  /** Cleared if the unit is deleted, so lease and payment history survive. */
  unitId?: string;
  propertyId?: string;
  /** 'fixed' = contract with an end date, 'month_to_month' = open-ended. */
  leaseType: LeaseType;
  startDate?: string;
  endDate?: string;
  monthlyRent: number;
  /** The move-in fee amount (labelled "Move-In Fee" in the UI). */
  securityDeposit?: number;
  /** Whether the move-in fee has been paid. Defaults to paid. */
  moveInFeePaid?: boolean;
  /** When the move-in fee was recorded paid, how, and its receipt. */
  moveInFeePaidDate?: string;
  moveInFeeMethod?: string;
  moveInFeeReceiptDocumentId?: string;
  /** Refundable security deposit amount (separate from the move-in fee). */
  depositAmount?: number;
  /** Whether the security deposit has been collected. */
  depositPaid?: boolean;
  /** When the deposit was collected. */
  depositPaidDate?: string;
  /** How the deposit was paid. */
  depositMethod?: string;
  status: LeaseStatus;
  needsReview?: boolean;
  /**
   * Every stretch collection was paused on this lease, oldest first. The
   * server stamps these itself when the status changes to or from 'paused'
   * (off the `statusChangedOn` the client sends), so the client never writes
   * to this list directly and cannot forget to or disagree with the
   * database. An entry with no `resumedAt` is an OPEN pause: rent stays
   * stopped from the month after `pausedAt`, with no upper bound, until a
   * later status change resumes or ends the lease. A lease can carry more
   * than one entry, since it can be paused, resumed, and paused again.
   * Symmetric with `endDate`: the whole month a pause starts or resumes in
   * is still owed in full, no proration; only the months strictly between
   * are excluded.
   */
  pauses: { pausedAt: string; resumedAt?: string }[];
  notes?: string;
  /** Why the tenancy ended, recorded when the office terminates it. */
  endReason?: string;
  /** Property and unit snapshot captured at termination, so the rental history
   * survives even if the unit is later renamed, reassigned, or deleted. */
  endedPropertyLabel?: string;
  endedUnitLabel?: string;
  tenantIds: string[];
  rentDueDay?: number;
  renewedFromLeaseId?: string;
  renewalGeneratedAt?: number;
  previousRent?: number;
  renewalStatus?: 'draft' | 'pending' | 'approved' | 'rejected' | 'cancelled';
}

export interface LeaseAuditEntry {
  id: string;
  leaseId: string;
  action: string;
  changedBy?: string;
  changedByName?: string;
  previousData?: Record<string, unknown>;
  newData?: Record<string, unknown>;
  notes?: string;
  createdAt: number;
}

export interface LeaseNotification {
  id: string;
  leaseId: string;
  tenantId?: string;
  tenantEmail?: string;
  tenantName?: string;
  notificationType: string;
  subject?: string;
  status: string;
  sentBy?: string;
  createdAt: number;
}

/** A person. Rent and lease dates live on the Lease. */
export interface Tenant {
  id: string;
  tenantNumber?: string;
  firstName: string;
  lastName: string;
  email?: string;
  phone?: string;
  notes?: string;
  emergencyContact?: {
    name: string;
    phone: string;
    relationship: string;
  };
  address?: {
    line1: string;
    line2: string;
    city: string;
    state: string;
    zip: string;
  };
  photoUrl?: string | null;
  hasLogin?: boolean;
  /** True once the tenant has successfully signed in to their portal at least once. */
  verified?: boolean;
}

export interface RentPayment {
  id: string;
  leaseId: string;
  paidByTenantId?: string;
  amount: number;
  dueDate?: string;
  paidDate?: string;
  receivedDate?: string;
  status: 'paid' | 'pending' | 'overdue' | 'partial';
  month: number;
  year: number;
  notes?: string;
  paymentMethod?: PaymentMethod;
  uploadedBy?: string;
  uploadedAt?: string;
  receiptDocumentId?: string;
  /** 'payment' (default) = real money in; 'credit' = balance reduction, not income. */
  type?: 'payment' | 'credit';
  /** Why the credit was given. Only set when type = 'credit'. */
  creditReason?: CreditReason;
}

export type CreditReason = 'proration' | 'maintenance' | 'balance' | 'other';

/** Links a portion of a payment to a specific rent period or late fee. */
export interface PaymentAllocation {
  id: string;
  paymentId: string;
  leaseId: string;
  month: number;
  year: number;
  amount: number;
  /** 'rent' = applied to monthly rent; 'late_fee' = applied to a late fee charge. */
  type: 'rent' | 'late_fee';
  createdAt?: number;
}

export type LateFeeStatus = 'outstanding' | 'paid' | 'partial' | 'waived';

/** A manually assessed late fee charge. */
export interface LateFee {
  id: string;
  leaseId: string;
  tenantId?: string;
  propertyId?: string;
  unitId?: string;
  month: number;
  year: number;
  amount: number;
  assessedDate: string;
  status: LateFeeStatus;
  waivedAt?: number;
  waivedBy?: string;
  waiveReason?: string;
  notes?: string;
  createdBy: string;
  createdAt?: number;
}

export type PaymentMethod =
  | 'check'
  | 'money_order'
  | 'zelle'
  | 'venmo'
  | 'cash'
  | 'bank_transfer'
  | 'card'
  | 'other';

export interface Expense {
  id: string;
  propertyId: string;
  unitId?: string;
  category: ExpenseCategory;
  amount: number;
  date: string;
  description: string;
  receipt?: string;
  /** Receipt image URL (served from Drive), null/undefined when none uploaded. */
  receiptUrl?: string | null;
  vendor?: string;
  isRecurring: boolean;
  recurringFrequency?: 'monthly' | 'quarterly' | 'yearly';
  taxDeductible?: boolean;
  taxCategory?: string;
  /** For a mortgage expense: the deductible interest portion of the payment.
   * The remainder (principal) is not tax-deductible. */
  interestAmount?: number;
  /** When this expense belongs to a capital improvement project. */
  capitalProjectId?: string;
  /** Optional explicit tier (property / unit / business). When absent the
   * frontend derives it from the category via `getExpenseTier()`. */
  tier?: ExpenseTier;
  /** Last 3-4 digits of the bank/card account used to pay this expense. */
  paymentAccount?: string;
  /** Explicit classification: 'operating' or 'capital'. When set, overrides
   *  all automatic classification logic. Null/undefined = legacy behavior. */
  expenseType?: 'operating' | 'capital';
  /** Review status: 'confirmed' = user made a deliberate choice,
   *  'needs_review' = flagged for admin review (e.g. ambiguous import). */
  classificationStatus?: 'confirmed' | 'needs_review';
  /** Per-expense IRS recovery period override for depreciation (e.g. 5, 7, 15, 27.5, 39).
   *  When set, overrides the category-based default in the Tax Report. */
  recoveryYears?: number;
}

export type TaxDeductibleCategory =
  | 'advertising'
  | 'auto_travel'
  | 'cleaning_maintenance'
  | 'commissions'
  | 'insurance'
  | 'legal_professional'
  | 'management_fees'
  | 'mortgage_interest'
  | 'other_interest'
  | 'repairs'
  | 'supplies'
  | 'taxes'
  | 'utilities'
  | 'depreciation'
  | 'other';

// ---------------------------------------------------------------------------
// Expense categories — organized into three tiers for financial reporting.
// ---------------------------------------------------------------------------

/** Property-level expense categories. */
export type PropertyExpenseCategory =
  | 'utilities'
  | 'hoa'
  | 'taxes'           // property taxes
  | 'insurance'
  | 'mortgage'
  | 'landscaping'
  | 'snow_removal'
  | 'pest_control'
  | 'capital_improvements'
  | 'repairs'
  | 'maintenance'
  | 'cleaning'
  | 'common_area'
  | 'property_improvements'
  | 'supplies';

/** Unit-level expense categories. */
export type UnitExpenseCategory =
  | 'tenant_repairs'
  | 'appliance_replacement'
  | 'turnover_costs'
  | 'unit_maintenance'
  | 'tenant_damage'
  | 'unit_improvements';

/** Business / management expense categories. */
export type BusinessExpenseCategory =
  | 'software'
  | 'office_expenses'
  | 'marketing'
  | 'accounting'
  | 'legal'
  | 'payroll'
  | 'administrative'
  | 'banking_fees'
  | 'other_business';

/** All expense categories (union of the three tiers + legacy catch-all). */
export type ExpenseCategory =
  | PropertyExpenseCategory
  | UnitExpenseCategory
  | BusinessExpenseCategory
  // Legacy categories still stored in older records.
  | 'realtor_commission'
  | 'management'
  | 'other';

/** Which tier a category belongs to. */
export type ExpenseTier = 'property' | 'unit' | 'business';

/** All income source categories. */
export type IncomeSource =
  | 'rent'
  | 'late_fee'
  | 'deposit'            // security deposit received
  | 'deposit_retained'   // portion of deposit kept as income (deductions)
  | 'move_in_fee'
  | 'utility_reimbursement'
  | 'hoa_reimbursement'
  | 'application_fee'
  | 'pet_fee'
  | 'parking_fee'
  | 'other';

export interface Income {
  id: string;
  propertyId: string;
  unitId?: string;
  tenantId?: string;
  source: IncomeSource;
  amount: number;
  date: string;
  description: string;
  paymentMethod?: PaymentMethod;
  relatedPaymentId?: string;
}

// The maintenance job lifecycle:
// submitted -> assigned -> scheduled -> in_progress -> completed -> paid
// (cancelled at any point). scheduled_for is set when a handyman confirms a time.
export type MaintenanceStatus =
  | 'submitted'
  | 'available'
  | 'assigned'
  | 'scheduled'
  | 'in_progress'
  | 'completed'
  // Invoice workflow statuses (after admin approves completed work).
  | 'approved_for_invoicing'
  | 'invoice_submitted'
  | 'invoice_approved'
  | 'paid'
  | 'cancelled';
export type MaintenancePriority = 'low' | 'medium' | 'high' | 'urgent';

/** The trade categories a handyman works and a request is filed under. */
export const MAINTENANCE_TRADES = [
  'plumbing',
  'electrical',
  'hvac',
  'appliance',
  'carpentry',
  'pest_control',
  'painting',
  'landscaping',
  'roofing',
  'flooring',
  'cleaning',
  'locksmith',
  'general',
  'other',
] as const;
export type MaintenanceTrade = (typeof MAINTENANCE_TRADES)[number];

/** A window when the tenant is home for the work. Local calendar day + times. */
export interface AvailabilityWindow {
  date: string; // YYYY-MM-DD
  start: string; // HH:MM
  end: string; // HH:MM
}

export interface MaintenanceRequest {
  id: string;
  propertyId?: string;
  unitId?: string;
  tenantId?: string;
  title: string;
  description?: string;
  category?: string;
  priority: MaintenancePriority;
  status: MaintenanceStatus;
  cost: number;
  vendor?: string;
  reportedDate?: string;
  resolvedDate?: string;
  notes?: string;
  // Maintenance portal fields.
  assignedHandymanId?: string;
  scheduledFor?: string;
  availability?: AvailabilityWindow[];
  paidAt?: string;
  createdBy?: 'tenant' | 'admin' | 'handyman';
  photoUrl?: string | null;
  /** True when a handyman self-reported this work and it awaits admin approval. */
  needsApproval?: boolean;
  /** The day an admin approved the job and its cost (expense written then). */
  approvedAt?: string;
  /** The cost the handyman proposed when reporting the work. */
  reportedCost?: number;
  // Invoice workflow fields.
  invoiceNumber?: string;
  invoiceDate?: string;
  invoiceLaborAmount?: number;
  invoiceMaterialAmount?: number;
  invoiceTotalAmount?: number;
  invoiceNotes?: string;
  /** Drive file references: { id, name, contentType } for each uploaded file. */
  invoiceDriveIds?: { id: string; name: string; contentType: string }[];
  invoiceSubmittedAt?: string;
  invoiceApprovedAt?: string;
  invoiceApprovedBy?: string;
  invoiceRejectionReason?: string;
  /** True when resolved under manufacturer warranty (no cost to landlord). */
  warrantyCovered?: boolean;
  /** The specific appliance this request was linked to for warranty tracking. */
  warrantyApplianceId?: string;
  /** Unix epoch seconds when the request was created. */
  createdAt?: number;
}

/** A single entry in the maintenance status history log. */
export interface MaintenanceStatusLog {
  id: string;
  requestId: string;
  fromStatus: string | null;
  toStatus: string;
  changedById?: string;
  changedByName?: string;
  notes?: string;
  createdAt: number;
}

export interface Handyman {
  id: string;
  userId?: string;
  name: string;
  companyName?: string;
  phone?: string;
  email?: string;
  address?: string;
  city?: string;
  state?: string;
  zipCode?: string;
  trades: string[];
  isActive: boolean;
  hasLogin: boolean;
  photoUrl?: string | null;
}

export interface DashboardStats {
  totalProperties: number;
  totalUnits: number;
  occupiedUnits: number;
  totalTenants: number;
  monthlyIncome: number;
  monthlyExpenses: number;
  netIncome: number;
  totalOwed: number;
  occupancyRate: number;
  projectedYearlyIncome?: number;
  /** Daily-prorated rent lost from vacant units this month. */
  vacancyLossThisMonth: number;
  /** Daily-prorated rent lost from vacant units year to date. */
  vacancyLossYTD: number;
  /** Gross potential rent (what every unit COULD earn) for the current month. */
  grossPotentialRentMonth: number;
  /** Vacancy rate: vacant unit-days / total available unit-days × 100. */
  vacancyRate: number;
  /** Number of units currently vacant and available for listing. */
  vacantAvailable: number;
  /** Number of units currently vacant but with a future lease (not available). */
  vacantPendingLease: number;
}

export type ViewType = 'dashboard' | 'properties' | 'tenants' | 'rents' | 'expenses' | 'income';

/** Roles that belong in the portal and must never reach the management app. */
export const PORTAL_ROLES = ['tenant', 'realtor', 'handyman'] as const;

export function isPortalRole(roleId?: string): boolean {
  return !!roleId && (PORTAL_ROLES as readonly string[]).includes(roleId);
}

export type CalendarCategory =
  | 'rent_due' | 'utility_due' | 'mortgage' | 'hoa' | 'property_tax' | 'insurance'
  | 'inspection' | 'smoke_detector' | 'hvac' | 'pest_control' | 'lawn_care' | 'snow_removal' | 'maintenance'
  | 'lease_expiration' | 'lease_renewal' | 'lease_termination' | 'move_in' | 'move_out'
  | 'contractor' | 'vendor' | 'licensing' | 'city_inspection'
  | 'warranty_expiration'
  | 'birthday' | 'personal' | 'custom';

export type CalendarPriority = 'low' | 'medium' | 'high' | 'urgent';
export type RecurrenceRule = 'daily' | 'weekly' | 'monthly' | 'quarterly' | 'semi_annually' | 'annually';

export interface CalendarEvent {
  id: string;
  propertyId?: string;
  /** All property IDs this event is assigned to (multi-property support). */
  propertyIds?: string[];
  unitId?: string;
  title: string;
  description?: string;
  category: CalendarCategory;
  eventDate: string;
  /** Optional end date for multi-day events (blocks the range). */
  endDate?: string;
  /** Optional time of day, e.g. "09:00" or "14:30". */
  eventTime?: string;
  /** Optional end time for duration events, e.g. "11:00" or "17:30". */
  endTime?: string;
  priority: CalendarPriority;
  isRecurring: boolean;
  recurrenceRule?: RecurrenceRule;
  completed: boolean;
  completedAt?: number;
  notes?: string;
  reminderHours?: number;
  createdAt?: number;
  /** True for auto-generated events (birthdays, lease milestones). Not editable. */
  isAuto?: boolean;
  /** 'shared' (everyone sees) or 'personal' (only creator sees). */
  visibility?: 'shared' | 'personal';
  /** The user who created this event. */
  userId?: string;
}

export interface PortalPayment {
  // The payment's own id and receipt, so a tenant can download or generate the
  // receipt for their own rent. Still never the payer (shared-lease privacy).
  id?: string;
  amount: number;
  dueDate?: string;
  paidDate?: string;
  status: 'paid' | 'pending' | 'overdue' | 'partial';
  month: number;
  year: number;
  // How the payment was recorded (cash, check, zelle, ...). The method only:
  // still never who paid, so the shared-lease privacy rule holds.
  paymentMethod?: PaymentMethod;
  receiptDocumentId?: string;
  /** 'payment' (default) or 'credit' (admin-applied balance reduction). */
  type?: 'payment' | 'credit';
  /** Reason for the credit. */
  creditReason?: CreditReason;
}

/** Comprehensive property profile returned by GET /api/properties/:id/profile. */
export interface PropertyProfile {
  property: {
    id: string;
    name: string;
    address: string;
    city: string;
    state: string;
    zipCode: string;
    type: string;
    description?: string;
    image?: string;
    purchaseDate?: string;
    purchasePrice?: number;
    landValue?: number;
    createdAt?: number;
  };
  units: Array<{
    id: string;
    unitNumber: string;
    bedrooms?: number;
    bathrooms?: number;
    squareFeet?: number;
    monthlyRent?: number;
  }>;
  leases: Array<{
    id: string;
    unitId?: string;
    unitNumber?: string;
    status: string;
    startDate?: string;
    endDate?: string;
    monthlyRent?: number;
    tenantNames?: string;
    tenantIds: string[];
    endReason?: string;
    renewalStatus?: string;
    moveInFeePaid?: boolean;
    securityDeposit?: number;
  }>;
  rentPayments: Array<{
    id: string;
    leaseId: string;
    unitNumber?: string;
    month: number;
    year: number;
    amount: number;
    status: string;
    paidDate?: string;
    paymentMethod?: string;
    type?: string;
    creditReason?: string;
  }>;
  expenses: Array<{
    id: string;
    category: string;
    amount: number;
    date: string;
    description?: string;
    vendor?: string;
    unitId?: string;
  }>;
  incomes: Array<{
    id: string;
    source: string;
    amount: number;
    date: string;
    description?: string;
  }>;
  maintenance: Array<{
    id: string;
    title: string;
    description?: string;
    status: string;
    priority?: string;
    unitNumber?: string;
    tenantName?: string;
    handymanName?: string;
    createdAt?: number;
    completedAt?: string | number | null;
    cost?: number;
  }>;
  documents: Array<{
    id: string;
    name: string;
    contentType?: string;
    size?: number;
    createdAt?: number;
    driveFileId?: string;
  }>;
  utilityAccounts: Array<{
    id: string;
    utilityType: string;
    provider?: string;
    accountNumber?: string;
    notes?: string;
  }>;
  financialSummary: Record<string, { income: number; expenses: number; net: number }>;
  auditLog: Array<{
    id: string;
    leaseId: string;
    action: string;
    changedByName?: string;
    previousData?: Record<string, unknown>;
    newData?: Record<string, unknown>;
    notes?: string;
    createdAt?: number;
  }>;
  calendarEvents: Array<{
    id: string;
    title: string;
    startDate?: string;
    endDate?: string;
    category?: string;
    notes?: string;
  }>;
  appliances: Array<{
    id: string;
    propertyId: string;
    unitId?: string;
    unitNumber?: string;
    type: string;
    brand?: string;
    modelNumber?: string;
    serialNumber?: string;
    purchaseDate?: string;
    warrantyExpiration?: string;
    condition: string;
    notes?: string;
  }>;
}

export type NoteCategory =
  | 'general' | 'property_management' | 'tenant_communication' | 'maintenance'
  | 'financial' | 'inspection' | 'legal' | 'compliance' | 'other';

export interface NoteAttachment {
  id: string;
  name: string;
  driveFileId: string;
  contentType?: string;
  size: number;
  uploadedBy?: string;
  uploadedByName?: string;
  uploadedAt: number;
}

export interface PropertyNote {
  id: string;
  propertyId: string;
  title: string;
  content: string;
  category: NoteCategory;
  isPinned: boolean;
  createdBy?: string;
  createdByName?: string;
  createdAt: number;
  updatedAt: number;
  updatedBy?: string;
  updatedByName?: string;
  attachments: NoteAttachment[];
}

// ---------------------------------------------------------------------------
// Expense Import (staging)
// ---------------------------------------------------------------------------

export type ImportStatus = 'staged' | 'validated' | 'merged' | 'rolled_back';
export type ImportRowStatus = 'pending' | 'valid' | 'error' | 'duplicate' | 'skipped';

export interface ExpenseImport {
  id: string;
  fileName: string;
  fileDriveId?: string;
  uploadedBy: string;
  uploadedByName?: string;
  uploadedAt: number;
  status: ImportStatus;
  totalRows: number;
  validRows: number;
  errorRows: number;
  duplicateRows: number;
  mergedRows: number;
  mergedAt?: number;
  mergedByName?: string;
  rolledBackAt?: number;
  rolledBackByName?: string;
  notes?: string;
}

export interface ImportRowError {
  field: string;
  message: string;
}

export interface ExpenseImportRow {
  id: string;
  importId: string;
  rowNumber: number;
  originalData: Record<string, string>;
  propertyId?: string;
  propertyName?: string;
  unitId?: string;
  unitName?: string;
  category?: string;
  amount?: number;
  date?: string;
  description?: string;
  vendor?: string;
  notes?: string;
  taxCategory?: string;
  taxDeductible: boolean;
  isRecurring: boolean;
  recurringFrequency?: string;
  interestAmount?: number;
  status: ImportRowStatus;
  errors: ImportRowError[];
  createdExpenseId?: string;
  edited: boolean;
}

export interface ExpenseImportDetail extends ExpenseImport {
  rows: ExpenseImportRow[];
}

// ---------------------------------------------------------------------------
// Capital Expense Projects
// ---------------------------------------------------------------------------

export type CapitalProjectStatus = 'in_progress' | 'completed' | 'cancelled';

export interface CapitalProject {
  id: string;
  name: string;
  propertyId: string;
  unitId?: string;
  description?: string;
  status: CapitalProjectStatus;
  startDate?: string;
  completionDate?: string;
  budget?: number;
  driveFolderId?: string;
  createdBy?: string;
  createdAt: number;
  updatedAt: number;
  recoveryYears?: number;
  placedInServiceDate?: string;
  /** Rolled-up total of all linked expenses (server-computed). */
  totalCost: number;
  /** Count of linked expenses (server-computed). */
  expenseCount: number;
}

export interface CapitalProjectDetail extends CapitalProject {
  expenses: Expense[];
}

// ---------------------------------------------------------------------------
// Security deposit returns
// ---------------------------------------------------------------------------

export type DepositReturnStatus = 'pending' | 'processing' | 'completed' | 'forfeited';
export type DeductionCategory = 'damage' | 'unpaid_rent' | 'cleaning' | 'repairs' | 'other';

export interface DepositReturn {
  id: string;
  leaseId: string;
  tenantId?: string;
  propertyId?: string;
  unitId?: string;
  depositAmount: number;
  moveOutDate: string;
  deadlineDate: string;
  status: DepositReturnStatus;
  totalDeductions: number;
  refundAmount: number;
  refundDate?: string;
  refundMethod?: string;
  notes?: string;
  createdAt: number;
  updatedAt: number;
  /** Populated by the list endpoint. */
  deductions?: DepositDeduction[];
  /** Resolved on the client for display. */
  tenantName?: string;
  propertyName?: string;
  unitNumber?: string;
}

export interface DepositDeduction {
  id: string;
  depositReturnId: string;
  category: DeductionCategory;
  description: string;
  amount: number;
  createdAt: number;
}

// ---------------------------------------------------------------------------
// Inspection reports
// ---------------------------------------------------------------------------
export type InspectionType = 'move_in' | 'move_out' | 'periodic';
export type InspectionStatus = 'draft' | 'completed';
export type ItemCondition = 'excellent' | 'good' | 'fair' | 'poor' | 'damaged' | 'na';

export interface Inspection {
  id: string;
  propertyId?: string;
  unitId?: string;
  leaseId?: string;
  tenantId?: string;
  type: InspectionType;
  inspectionDate: string;
  inspectorName?: string;
  status: InspectionStatus;
  notes?: string;
  driveFileId?: string;
  createdAt: number;
  updatedAt: number;
  items?: InspectionItem[];
  /** Resolved on the client for display. */
  tenantName?: string;
  propertyName?: string;
  unitNumber?: string;
}

export interface InspectionItem {
  id: string;
  inspectionId: string;
  room: string;
  item: string;
  condition: ItemCondition;
  notes?: string;
  photoDriveId?: string;
  createdAt: number;
}

// ---------------------------------------------------------------------------
// Notices / letters
// ---------------------------------------------------------------------------
export type NoticeType = 'late_rent' | 'pre_5_day' | 'violation' | 'non_renewal' | 'rent_increase' | 'custom';
export type NoticeStatus = 'draft' | 'sent' | 'acknowledged';
export type DeliveryMethod = 'hand_delivered' | 'posted' | 'email' | 'certified_mail';

export interface Notice {
  id: string;
  propertyId?: string;
  unitId?: string;
  leaseId?: string;
  tenantId?: string;
  type: NoticeType;
  title: string;
  body: string;
  noticeDate: string;
  deliveryMethod?: DeliveryMethod;
  deliveredAt?: string;
  status: NoticeStatus;
  driveFileId?: string;
  createdBy?: string;
  createdAt: number;
  updatedAt: number;
  /** Resolved on the client for display. */
  tenantName?: string;
  propertyName?: string;
  unitNumber?: string;
}

// ── Task Management ──

export type TaskStatus = 'todo' | 'in_progress' | 'waiting' | 'completed' | 'cancelled';
export type TaskPriority = 'low' | 'medium' | 'high' | 'urgent';
export type TaskCategory =
  | 'maintenance' | 'tenant' | 'leasing' | 'finance' | 'vendor'
  | 'compliance' | 'management' | 'administrative' | 'marketing'
  | 'technology' | 'projects' | 'other';

export type ApprovalStatus = 'not_required' | 'pending' | 'approved' | 'rejected';

export interface Task {
  id: string;
  title: string;
  description?: string;
  category: TaskCategory;
  status: TaskStatus;
  priority: TaskPriority;
  waitingFor?: string;
  assignedTo?: string;
  assignedToName?: string;
  createdBy: string;
  createdByName?: string;
  dueDate?: string;
  dueTime?: string;
  startDate?: string;
  estimatedMinutes?: number;
  actualMinutes?: number;
  projectId?: string;
  projectName?: string;
  propertyId?: string;
  propertyName?: string;
  unitId?: string;
  unitNumber?: string;
  tenantId?: string;
  tenantName?: string;
  vendorId?: string;
  vendorName?: string;
  leaseId?: string;
  maintenanceRequestId?: string;
  expenseId?: string;
  inspectionId?: string;
  requiresApproval: boolean;
  approvalStatus: ApprovalStatus;
  approvedBy?: string;
  approvedAt?: number;
  isRecurring: boolean;
  recurrenceRule?: string;
  recurrenceEndDate?: string;
  parentTaskId?: string;
  completedAt?: number;
  completedBy?: string;
  tags?: TaskTag[];
  createdAt: number;
  updatedAt: number;
}

export interface TaskTag {
  id: string;
  name: string;
  color?: string;
}

export interface TaskComment {
  id: string;
  taskId: string;
  userId: string;
  userName?: string;
  body: string;
  createdAt: number;
}

export interface TaskActivityEntry {
  id: string;
  taskId: string;
  action: string;
  fromValue?: string;
  toValue?: string;
  userId: string;
  userName?: string;
  createdAt: number;
}

export type ProjectStatus = 'planning' | 'active' | 'on_hold' | 'completed' | 'cancelled';

export interface Project {
  id: string;
  name: string;
  description?: string;
  status: ProjectStatus;
  priority: TaskPriority;
  ownerId?: string;
  ownerName?: string;
  startDate?: string;
  dueDate?: string;
  propertyId?: string;
  propertyName?: string;
  createdBy: string;
  createdByName?: string;
  taskCount?: number;
  completedCount?: number;
  createdAt: number;
  updatedAt: number;
}

export interface TaskTemplate {
  id: string;
  name: string;
  description?: string;
  category?: string;
  createdBy: string;
  items?: TaskTemplateItem[];
  createdAt: number;
  updatedAt: number;
}

export interface TaskTemplateItem {
  id: string;
  templateId: string;
  title: string;
  description?: string;
  category?: string;
  priority: TaskPriority;
  sortOrder: number;
  estimatedMinutes?: number;
}
